import { Stack, Typography } from '@mui/material';
import { useIntl } from 'react-intl';

import {
  ApiWorkflowDialog,
  type ApiWorkflowDialogMessages,
  DialogMessageBox,
} from '@verantos-dev/shared-components';

import { useDeleteAnalysisDialog } from '../hooks/useDeleteAnalysisDialog';

interface DeleteAnalysisDialogProps {
  projectId: string;
  analysisProfileId: string;
  name: string;
  onClose: () => void;
}

/**
 * Inline delete-confirmation dialog opened from an analysis row on the
 * artifacts page. Uses the `analysisList.deleteModal.*` message ids —
 * registered app-wide via `analysisMessageKeysEn` — so the copy is owned by
 * feature-analysis without this lib importing it.
 */
export const DeleteAnalysisDialog = ({
  projectId,
  analysisProfileId,
  name,
  onClose,
}: DeleteAnalysisDialogProps) => {
  const { formatMessage } = useIntl();
  const { onSubmit, closeDialog, isPending, submittingError, isValidToSubmit } =
    useDeleteAnalysisDialog({ projectId, analysisProfileId, onClose });

  const dialogMessages: ApiWorkflowDialogMessages = {
    entryCancelButtonText: formatMessage({ id: 'analysisList.deleteModal.cancelButton' }),
    entrySaveButtonText: formatMessage({ id: 'analysisList.deleteModal.deleteButton' }),
    submittingCancelButtonText: formatMessage({
      id: 'analysisList.deleteModal.cancelButton',
    }),
    submittingSaveButtonText: formatMessage({ id: 'analysisList.deleteModal.submitting' }),
    successCloseButtonText: formatMessage({ id: 'analysisList.deleteModal.closeButton' }),
    submittingErrorTitle: formatMessage({ id: 'analysisList.deleteModal.submittingError' }),
    successTitle: formatMessage({ id: 'analysisList.deleteModal.success.title' }),
    successText: formatMessage({ id: 'analysisList.deleteModal.success.text' }, { name }),
  };

  return (
    <ApiWorkflowDialog
      id="deleteAnalysisDialog"
      title={formatMessage({ id: 'analysisList.deleteModal.title' })}
      dialogMessages={dialogMessages}
      onClose={closeDialog}
      onSubmit={onSubmit}
      saveButtonColor="destructive"
      isValidForSubmit={isValidToSubmit}
      isSubmitting={isPending}
      submittingError={submittingError}
      hideDisplayButtonsOnSuccess
    >
      <DialogMessageBox
        id="delete-analysis-message-box"
        title={formatMessage({ id: 'analysisList.deleteModal.confirmTitle' })}
        type="error"
        variant="outlined"
        renderSubmitted={
          <Stack gap={4}>
            <Typography color="error">
              {formatMessage({ id: 'analysisList.deleteModal.actionUndone' })}
            </Typography>
            <Typography>
              {formatMessage(
                { id: 'analysisList.deleteModal.description' },
                { name: <b>{name}</b> }
              )}
            </Typography>
          </Stack>
        }
      />
    </ApiWorkflowDialog>
  );
};

export default DeleteAnalysisDialog;
